import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Container, Typography, Box, Card, CardContent, CircularProgress,
    Alert, Grid, List, ListItem, ListItemText, Divider, Button, Stack,
    Tooltip, Chip
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload'; 

// NOTE: Placeholder for your custom Page component (e.g., a layout wrapper)
const Page = ({ children, title }) => { 
    useEffect(() => {
        document.title = title;
    }, [title]);
    return <Box sx={{ pt: 3, pb: 5 }}>{children}</Box>;
};


// --- API Base URL ---
const BASE_URL = 'https://ipms.insollex.com/api';

// Fields to display in the Key Details table
const MATTER_SUMMARY_FIELDS = [
    'dateSubmitted', 
    'appointedProvisionalLiquidators', 
    'dateOfProvisionalLiquidation', 
    'masterOverseeingTheEstate'
];

// Helper to check if ID looks like a new UUID or an old Estate ID (numeric)
const isUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id));

// Helper to format keys for display (e.g., 'dateSubmitted' -> 'Date Submitted')
const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());


// --- Component for AI Summary Section ---
const AISummaryCard = ({ summary }) => (
    <Card sx={{ height: '100%', mb: 3 }}>
        <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>SUMMARY:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {summary}
            </Typography>
        </CardContent>
    </Card>
);

// --- Component for Key Details Section ---
const KeyDetailsCard = ({ matter }) => (
    <Card sx={{ height: '100%', mb: 3 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
                Key Details
            </Typography>
            <List dense disablePadding>
                {/* NOTE: matter should be guaranteed to be an object by the parent component's checks */}
                {Object.entries(matter).filter(([key]) => MATTER_SUMMARY_FIELDS.includes(key))
                    .map(([key, value]) => (
                        <ListItem key={key} disablePadding sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {formatKey(key)}:
                                    </Typography>
                                }
                                secondary={<Typography variant="body2" color="text.secondary">{String(value || 'N/A')}</Typography>}
                            />
                        </ListItem>
                    ))
                }
            </List>
        </CardContent>
    </Card>
);

// --- Component for Documents List Section ---
const DocumentsCard = ({ documents, token }) => { 
    // 🛡️ FIX: Create a safe array. If documents is null or undefined, use an empty array instead.
    const safeDocuments = Array.isArray(documents) ? documents : [];

    const handleDownload = (document) => {
        // Pass the token to the download endpoint
        const downloadLink = `${BASE_URL}/documents/download/${document.id}?token=${token}`;
        window.open(downloadLink, '_blank');
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                    RELEVANT DOCUMENTS
                </Typography>
                <List disablePadding>
                    {/* Use the safe array for length check and map */}
                    {safeDocuments.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 1 }}>No relevant documents found.</Alert>
                    ) : (
                        safeDocuments.map((doc, index) => (
                            <React.Fragment key={doc.id || index}>
                                <ListItem
                                    secondaryAction={
                                        <Tooltip title="Download Document">
                                            <Button 
                                                onClick={() => handleDownload(doc)}
                                                startIcon={<FileDownloadIcon />}
                                                size="small"
                                                variant="contained"
                                                color="secondary"
                                            >
                                                Download
                                            </Button>
                                        </Tooltip>
                                    }
                                    sx={{ py: 1, pr: 0 }}
                                >
                                    <ListItemText
                                        primary={doc.documentName || doc.FileName || `Document ${index + 1}`}
                                        secondary={
                                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                                                [{doc.dateCreated ? new Date(doc.dateCreated).toLocaleDateString() : doc.dateCreated || 'N/A'}]
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {index < safeDocuments.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))
                    )}
                </List>
            </CardContent>
        </Card>
    );
};


export default function MatterPublicSummaryPage() {
    const { id } = useParams();
    const location = useLocation(); 
    
    // Extract the token from the URL query string: ?token=...
    const searchParams = new URLSearchParams(location.search);
    const inviteToken = searchParams.get('token'); 

    const [matter, setMatter] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!id) {
            setError('No Matter ID provided.');
            setLoading(false);
            return;
        }

        if (!inviteToken) {
            setError('Access Denied: Missing invitation token.');
            setLoading(false);
            return;
        }

        const fetchPublicMatterData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const publicApiUrl = `${BASE_URL}/matter-public/${id}?token=${inviteToken}`;
                
                const res = await axios.get(publicApiUrl);
                
                const matterData = res.data.matter;
                const docsData = res.data.documents;

                if (!matterData) {
                    throw new Error('Matter data not found in response.');
                }

                setMatter(matterData);
                setDocuments(docsData);

            } catch (err) {
                console.error('Error fetching public matter data:', err);
                // Better error handling for axios errors
                setError(`Access failed: ${err.response?.data?.message || err.message || 'Unknown network error'}.`);
            } finally {
                setLoading(false);
            }
        };

        fetchPublicMatterData();
    }, [id, inviteToken]);

    const aiSummaryText = matter?.aiSummary || "Summary data not available for this matter.";
    const matterName = matter?.matterName || matter?.Name || id; 

    // --- RENDER LOGIC ---

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh">
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading Matter Data...</Typography>
                <Typography variant="caption" sx={{ mt: 1 }}>Checking token access...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ py: 5, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mt: 3, textAlign: 'left' }}>
                    <Typography variant="h6">Access Error</Typography>
                    **Reason:** {error}
                    <br />
                    <Typography variant="caption">Please ensure your link is correct and has not expired.</Typography>
                </Alert>
            </Container>
        );
    }
    
    if (!matter) {
        return (
            <Container maxWidth="sm" sx={{ py: 5, textAlign: 'center' }}>
                <Alert severity="warning" sx={{ mt: 3 }}>Matter with ID "{id}" not found or access has expired.</Alert>
            </Container>
        );
    }

    // --- SUCCESSFUL RENDER ---
    return (
        <Page title={`Matter Summary | ${matterName}`}>
            <Container maxWidth={false}>
                {/* --- HEADER --- */}
                <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 700 }}>
                    {matterName}
                    <Chip label="Public Access" color="success" size="small" sx={{ ml: 2 }} />
                </Typography>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    You are viewing this matter via a secure, unauthenticated link. No login is required.
                </Alert>
                
                <Grid container spacing={3}>
                    
                    {/* LEFT COLUMN: Summary & Key Details (md=7) */}
                    <Grid item xs={12} md={7}>
                        <AISummaryCard summary={aiSummaryText} />
                        <KeyDetailsCard matter={matter} /> 
                    </Grid>

                    {/* RIGHT COLUMN: Documents (md=5) */}
                    <Grid item xs={12} md={5}>
                        <DocumentsCard documents={documents} token={inviteToken} /> 
                    </Grid>
                </Grid>
            </Container>
        </Page>
    );
}
